import { ORG_APIS } from "@/api_framework/api_config";
import { IOrgProvidersListResponse } from "@/api_framework/api_modals/Org";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";

import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { setConfOrgProviders } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { setEMROrgProviders } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { setInventoryOrgProviders } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { setOrgProviders } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { setROTAOrgProviders } from "@/app_redux/reducers/slices/auth/rota_slice";
import { setSlotsOrgProviders } from "@/app_redux/reducers/slices/auth/slots_slice";
import { setCALENDAROrgProviders } from "@/app_redux/reducers/slices/auth/staff_calendar_slice";
import { setUIOrgProviders } from "@/app_redux/reducers/slices/ui_states_slice";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { AllowedCityClinicProviderRoutes } from "./useOrgCitiesListAPI";

AxiosCurlirize(axios, () => {});
export type ConfigurationOptionsProvidersAPI = {
  ROTADate: string;
  clinicIds?: Array<string>;
  serviceId?: string | string[];
  cityId?: string | string[];
  user_id?: string;
  start_time?: string;
  end_time?: string;
  booking_date?: string;
};

type PayloadProviderAPI = {
  booking_date: string;
  clinic_ids?: Array<string>;
  service_id?: string[];
  city_id?: string | string[];
  user_id?: string;
  is_only_doctor?: boolean;
  start_time?: string;
  end_time?: string;
};

export const useOrgClinicProvidersAPI = (forRoute: AllowedCityClinicProviderRoutes) => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const execute = useCallback(
    async (configurationOptions: ConfigurationOptionsProvidersAPI) => {
      dispatch(
        setLoading({
          key: "isOrgClinicProvidersInProgress",
          status: true,
        }),
      );
      let payload: PayloadProviderAPI = {
        clinic_ids: configurationOptions?.clinicIds ?? [],
        booking_date: configurationOptions?.ROTADate,
        is_only_doctor: forRoute === "EMR" ? true : undefined,
        start_time: configurationOptions?.start_time,
        end_time: configurationOptions?.end_time,
      };

      const normalizedServiceIds = Array.isArray(configurationOptions?.serviceId)
        ? configurationOptions?.serviceId?.filter((id) => id !== "")
        : configurationOptions?.serviceId && configurationOptions?.serviceId !== ""
          ? [configurationOptions?.serviceId]
          : [];

      const hasServiceIdFilter = normalizedServiceIds.length > 0;

      if (hasServiceIdFilter) {
        payload = { ...payload, service_id: normalizedServiceIds };
      }

      if (
        (typeof configurationOptions?.cityId === "string" && configurationOptions?.cityId !== "") ||
        (Array.isArray(configurationOptions?.cityId) && configurationOptions?.cityId?.length > 0)
      ) {
        payload = { ...payload, city_id: configurationOptions?.cityId };
      }
      if (forRoute === "SLOTS" && configurationOptions?.user_id) {
        payload = { ...payload, user_id: configurationOptions?.user_id };
      }
      if (
        forRoute === "SLOTS" &&
        configurationOptions?.user_id &&
        ((typeof configurationOptions?.cityId === "string" && configurationOptions?.cityId !== "") ||
          (Array.isArray(configurationOptions?.cityId) && configurationOptions?.cityId?.length > 0))
      ) {
        payload = { ...payload, user_id: configurationOptions?.user_id };
      }
      let API_URL = ORG_APIS?.PROVIDERS_LIST?.baseURL;
      if (forRoute === "PATIENT_WAITING_LIST") {
        API_URL = `${ORG_APIS?.PROVIDERS_LIST?.baseURL}?action=without_rota`;
      }

      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        await axios
          .post(
            API_URL ?? "",
            {
              ...payload,
            },
            {
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          )
          .then((res: AxiosResponse<IOrgProvidersListResponse>) => {
            dispatch(
              setLoading({
                key: "isOrgClinicProvidersInProgress",
                status: false,
              }),
            );
            if (res.data.status === true) {
              switch (forRoute) {
                case "UI":
                  dispatch(
                    setUIOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "EMR":
                  dispatch(
                    setEMROrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "INVENTORY":
                  dispatch(
                    setInventoryOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "PATIENT_WAITING_LIST":
                  dispatch(
                    setOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "CONFIGURATION":
                  dispatch(
                    setConfOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "ROTA":
                  dispatch(
                    setROTAOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "SLOTS":
                  dispatch(
                    setSlotsOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                case "CALENDAR":
                  dispatch(
                    setCALENDAROrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
                default:
                  dispatch(
                    setOrgProviders({
                      data: res.data.data,
                    }),
                  );
                  break;
              }
            } else {
              dispatch(
                setOrgProviders({
                  data: [],
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            sendCurl(e.config["curlCommand"]);
            dispatch(
              setLoading({
                key: "isOrgClinicProvidersInProgress",
                status: false,
              }),
            );
            captureError(e, false);
          });
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch],
  );
  return execute;
};
